import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IEvent } from 'app/interfaces/event.interface';
import { ITaxonomy } from 'app/interfaces/search.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
import { ContactsService } from 'app/services/contacts.service';
import { GlobalService } from 'app/services/global.service';
import { SearchStatsEvents, StatsService } from 'app/services/stats.service';
import { TaxonomyService } from 'app/services/taxonomy.service';
import { UserService } from 'app/services/user.service';
import { ZkuService } from 'app/services/zku.service';
import { SelectedClient } from 'app/shared/client-search/client-search.component';
import moment from 'moment';
import { LazyLoadEvent } from 'primeng/api';

@Component({
	selector: 'client-event-list',
	templateUrl: './client-event-list.component.html',
	styleUrls: ['./client-event-list.component.scss'],
})
export class ClientEventList implements OnInit {
	@Input()
	clientId: string;
	@Input()
	isContact: boolean;
	@Input()
	startWithDate = true;
	@Input()
	showClientSearch = true;
	@Input()
	invertOrder = false;
	@Input()
	withDateLimit = false;
	@Output()
	public selectedEvent = new EventEmitter<string>();

	userPref: IUserPreferences;
	// search data
	periodDate?: Date[] | null = [];
	maxPeriodDate: Date = null;
	hasValidDates = true;
	statsSearch: SearchStatsEvents;
	// Table data
	paginatorTemplate: string;
	limit = 10;
	stats: IEvent[];
	total = 0;
	totalRevenue = 0;
	// taxonomy
	reasons: ITaxonomy[];
	// control
	isMobile = false;
	isLoading = false;
	isLoadingReport = false;
	isProfessionalZen = false;

	// Constructor.
	constructor(
		protected global: GlobalService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected translate: TranslateService,
		protected contacts: ContactsService,
		protected user: UserService,
		protected statsService: StatsService,
		protected taxonomy: TaxonomyService,
		private zkuService: ZkuService
	) {
		// Get user preferences.
		this.userPref = this.user.getUserLocalPreferences();
		const userService = this.zkuService.getZku();

		if (
			userService !== undefined &&
			userService?.tags?.some((tag) => tag['id'] === 'professional_zen')
		) {
			this.isProfessionalZen = true;
		}
	}

	// On Init.
	public ngOnInit(): void {
		// Loader.
		this.isLoading = true;
		const tz = this.user.getUserLocal().timezone;

		if (this.startWithDate) {
			const start = moment.tz(tz).subtract(1, 'month').set('date', 24).toDate();
			const end = moment.tz(tz).set('date', 23).toDate();
			this.periodDate.push(start);
			this.periodDate.push(end);
		} else {
			this.periodDate.push(null);
			this.periodDate.push(null);
		}

		if (this.withDateLimit) {
			this.setPeriodLimits();
		}

		this.statsSearch = {
			skip: 0,
			limit: 0,
			minDate: this.periodDate[0],
			maxDate: this.periodDate[1],
			order: this.invertOrder ? 'day DESC' : undefined,
		};

		// Verify if is a mobile device.
		this.isMobile = this.global.isMobileDevice();
		this.paginatorTemplate = this.translate.instant(
			'PAGES.FINANCIALS.PAGINATOR'
		);

		// map reasons
		this.taxonomy.getTaxonomyList('reason').subscribe({
			next: (data) => {
				this.reasons = data;
				// Load
				this.isLoading = false;
				this.loadData();
			},
		});
	}

	// On window resize.
	public onResize(): void {
		// Update mobile info.
		this.isMobile = this.global.isMobileDevice();
	}

	setPeriodLimits() {
		if (this.withDateLimit) {
			const tz = this.user.getUserLocal().timezone;
			const start = moment(this.periodDate[0]).tz(tz);
			const end = moment(this.periodDate[1]).tz(tz);

			const min = end.clone().subtract(3, 'month').add(1, 'day');
			const max = start.clone().add(3, 'month').subtract(1, 'day');

			if (start.isBefore(min)) {
				this.periodDate[1] = null;
			}

			this.maxPeriodDate = start.isValid() ? max.toDate() : null;

			this.hasValidDates =
				start.isBefore(end) && end.isSameOrBefore(max) && !!this.maxPeriodDate;
		}
	}

	resetData() {
		this.stats = [];
		this.total = 0;
		this.totalRevenue = undefined;
		this.isLoading = false;
		this.isLoadingReport = false;
	}

	exportReport() {
		if (!this.isLoadingReport && this.hasValidDates) {
			this.isLoadingReport = true;
			if (this.clientId) {
				this.statsSearch.profileId = this.clientId;
			}
			this.statsSearch = {
				...this.statsSearch,
				minDate: this.periodDate[0],
				maxDate: this.periodDate[1],
			};

			this.statsService.getFinanceReport(this.statsSearch).subscribe(
				(res) => {
					this.isLoadingReport = false;
					window.open(res);
				},
				() => {
					this.resetData();
				}
			);
		}
	}

	loadData(event?: LazyLoadEvent) {
		if (!this.isLoading && this.hasValidDates) {
			this.isLoading = true;
			if (this.clientId && this.isContact) {
				this.contacts.getEvents(Number(this.clientId), 0, 10).subscribe(
					(res) => {
						this.stats = this.mapEvents(res.items);
						this.total = res.total;
						this.totalRevenue = 0;
						this.isLoading = false;
					},
					() => {
						this.resetData();
					}
				);
			} else {
				if (this.clientId) {
					this.statsSearch.profileId = this.clientId;
				}
				this.statsSearch = {
					...this.statsSearch,
					minDate: this.periodDate[0],
					maxDate: this.periodDate[1],
					skip: event ? event.first : 0,
					limit: this.limit,
				};

				this.statsService.getAppointmentStats(this.statsSearch).subscribe(
					(res) => {
						this.stats = this.mapEvents(res.items);
						this.total = res.total;
						this.totalRevenue = res.totalRevenue;
						this.isLoading = false;
					},
					() => {
						this.resetData();
					}
				);
			}
		}
	}

	mapEvents(events: IEvent[]) {
		return events.map((e) => {
			if (e.contactReason && e.contactReason.length > 0) {
				const aux: string[] = [];
				e.contactReason.forEach((r) => {
					const f = this.reasons.find((reason) => reason.id === r);
					if (f) {
						aux.push(f.name);
					}
				});
				e.contactReason = aux;
			}
			return e;
		});
	}

	public selectedClient(val?: SelectedClient): void {
		if (val) {
			this.statsSearch.profileId = val.profileId;
		} else {
			this.statsSearch.profileId = undefined;
		}
	}

	public showEventDetails(eventId: string) {
		this.selectedEvent.emit(eventId);
	}

	getFinalCompensation(event: IEvent): number | false {
		const finalCompensation = event.compensationFinal;
		return finalCompensation && finalCompensation.compensation !== undefined
			? finalCompensation && finalCompensation.compensation
			: false;
	}
}
